import React, { FC, ReactElement, useState, useEffect, useRef } from 'react';
import cns from 'classnames';
import Swiper from 'react-id-swiper';
import { useSelector } from 'react-redux';
import { goToSlide } from '../../../../helpers/swiper';
import { getScheduleStreams } from '../../../../stores/reducers/userData';
import { getDates, limitRender } from './mappings';
import Item, { EpisodeDto } from './Item/Item';
import './Schedule.sass';
import { ParamsDto, internalizationState } from 'src/stores/reducers/sections';
import localeFieldResolver from '../../../../helpers/locale-field-finder';

interface Props {
  className?: string;
  bgSettings?: {
    color?: string;
    isDark?: boolean;
  };
  sectionName: string;
  sectionId: string;
  sectionData?: Array<ParamsDto>;
}

const Schedule: FC<Props> = ({ className, bgSettings, sectionName, sectionId, sectionData }): ReactElement => {
  const { activeLangState } = useSelector(internalizationState);
  const streams = useSelector(getScheduleStreams);
  const [curSlide, setCurSlide] = useState<number>(0);
  const [streamsToRender, setStreamsToRender] = useState<Array<any>>();
  const [activeStreamEpisodesCount, setActiveStreamEpisodesCount] = useState<number>(0);
  const showMorePer = 8;
  const [showMore, setShowMore] = useState<number>(showMorePer);
  const dates: any = streams && getDates(streams, activeLangState);
  const [curDate, setCurDate] = useState(dates?.length && dates[0][0]);

  const swiperRef = useRef(null);

  const updateCurDateEpisodes = () => {
    const filteredStreams = limitRender(streams!, showMore, curDate, activeLangState)?.filter((stream: any) => stream.episode && stream.episode.length > 0) ?? [];
    setStreamsToRender(filteredStreams);
  };

  useEffect(updateCurDateEpisodes, [curDate, streams, showMore, activeLangState]);

  const isHaveEpisodesForCurrentDate = ({id}: {id: number}): boolean => {
    return streamsToRender?.find((s) => s.id === id)?.episode?.length > 0 ?? false;
  };

  useEffect(() => {
    updateStreamsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateStreamsCount(curSlide);
    setShowMore(showMorePer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curSlide])

  useEffect(() => {
    if (dates?.length) setCurDate(dates[0][0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLangState]);

  useEffect(() => {
    updateStreamsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamsToRender]);

  const updateStreamsCount = (streamIndex = 0): void => {
    if (streamsToRender && streamsToRender.length > 0) {
      const id: number = streamsToRender[streamIndex]?.id;
      const stream = streams?.find((stream: any) => stream.id === id) ?? {};
      setActiveStreamEpisodesCount(stream?.episode.length || 0);
    }
  }

  const updateCurrentDateTab = (date: string) => {
    setCurDate(date);
    setTimeout(() => {
      changeSlide(0);
      setShowMore(showMorePer);
    }, 0)
  };

  const streamDescriptions = streams?.map((stream) => stream.description_ru);
  const isMultiDescriptionStreams = streamDescriptions && streamDescriptions.some((stream, ind) => streamDescriptions?.indexOf(stream) !== ind);
  const streamsLength = streams && streams.length;
  const isShowMoreBtn = streams && streamsToRender ? activeStreamEpisodesCount > showMore : false;

  const handleShowMore = () => {
    const addEp = showMorePer * (streamsLength || 0);
    setShowMore(showMore + addEp);
  };

  const changeSlide = (n: number) => {    
    goToSlide(swiperRef, n);
  };

  const swiperParams = {
    watchOverflow: true,
    slidesPerView: 'auto' as 'auto',
    shouldSwiperUpdate: true,
    on: {
      slideChange: (e: any) => {
        setCurSlide(e.realIndex);
        setShowMore(showMorePer);
      },
    },
  };

  const isSpeakersVertical = (): boolean => {
    const speakersVisibilityParam = sectionData!.find((param) => param.className === 'is-speakers-vertical');
    return speakersVisibilityParam && 'checked' in speakersVisibilityParam ? speakersVisibilityParam.checked as boolean : true;
  };

  return (
    <section
      className={cns(
        'inner-section',
        'inner-schedule',
        bgSettings && bgSettings.isDark && 'inner-section--dark-bg',
        className
      )}
      style={{ backgroundColor: bgSettings && bgSettings.color }}
    >
      <div className="container" id={sectionId}>
        <div className="inner-schedule-head">
          <div className="section-title">{sectionName}</div>
          {dates && (
            <ul className="schedule-dates">
              {dates.map(([date]: any[], index: number) => (
                <li key={index} onClick={() => updateCurrentDateTab(date)} className={curDate === date ? 'active' : ''}>
                  <span>{date}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={cns('schedule-columns', isMultiDescriptionStreams && 'schedule-columns--two-cols')}>
          {streams && (
            <ul className="sh-col-places">
              {
                streams.filter((stream: any) => stream.episode && stream.episode.length > 0 && isHaveEpisodesForCurrentDate(stream)).map((stream: any, ind: number) => (
                    <li key={ind} className={ind === curSlide ? 'active' : ''} onClick={() => changeSlide(ind)}>
                      {localeFieldResolver(stream, 'description_ru', activeLangState)}
                    </li>
                  )
                )
              }
            </ul>
          )}
          {streamsToRender && (
            <div className="schedule-scroll-wrapper">
              <Swiper ref={swiperRef} {...swiperParams}>
                {
                  streamsToRender?.map((stream: any, index: number) => (
                      <div key={stream.id} className={stream.id + index}>
                          <ul className="schedule-list">
                            {stream.episode.map((episode: EpisodeDto) => (<Item key={episode.id} data={episode} isVertical={isSpeakersVertical()} sectionData={sectionData} />))}
                          </ul>
                      </div>
                    )
                  )
                }
              </Swiper>
            </div>
          )}
        </div>
        {isShowMoreBtn && (
          <div className="btn-more-wrap">
            <button className="btn btn-primary" type="button" onClick={handleShowMore}>
              {activeLangState === 'ru' ? 'Ещё' : 'More'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Schedule;
